import { z } from 'zod'

/**
 * End-user theme: per-customer colours, typeface, logo and hero for the
 * end-user flow and the postcard font. Stored in `end_user_theme`.
 * Distinct from Brand (folder key) and from the gift-portal Customer theme.
 */

const HexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/)

// The resolved family is interpolated straight into a <style> tag (@font-face)
// and into the root's inline font-family, so a quote or a brace would let a
// stored value write its own CSS over the end-user page. A CSS family name
// needs none of that: letters, digits, spaces, hyphens, dots, underscores.
const FontFamilySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9][A-Za-z0-9 ._-]*$/)

// One stored role -> the CSS custom properties it sets on `.theme-default`.
// reason: `--base-button` duplicates `--color-base-button` so Tailwind's
// `bg-base-button` utility resolves, and `--color-foreground` (text-foreground on
// the privacy note) equals `--color-foreground-light`; both brand CSS files keep
// each pair equal, so one stored value feeds both names.
export const END_USER_THEME_COLOR_VARS = {
  colorBackground: ['--color-background'],
  colorBackgroundLight: ['--color-background-light'],
  colorPrimary: ['--color-primary'],
  colorPrimaryForeground: ['--color-primary-foreground'],
  colorBaseButton: ['--color-base-button', '--base-button'],
  colorSecondary: ['--color-secondary'],
  colorForegroundLight: ['--color-foreground-light', '--color-foreground'],
  colorSlate50: ['--color-slate-50'],
  colorBaseInput: ['--color-base-input'],
  colorBorder: ['--color-border'],
  colorMutedForeground: ['--color-muted-foreground'],
  colorDestructive: ['--color-destructive'],
} as const

export type EndUserThemeColorRole = keyof typeof END_USER_THEME_COLOR_VARS

export const END_USER_THEME_COLOR_ROLES = Object.keys(
  END_USER_THEME_COLOR_VARS,
) as EndUserThemeColorRole[]

const colorFields = Object.fromEntries(
  END_USER_THEME_COLOR_ROLES.map((role) => [
    role,
    HexColorSchema.nullable().optional(),
  ]),
) as Record<EndUserThemeColorRole, z.ZodOptional<z.ZodNullable<z.ZodString>>>

export const ColorSchemeSchema = z.enum(['light', 'dark'])

/**
 * `color-scheme` is what native controls -- the date picker, the checkbox -- paint
 * themselves against. It is not a light/dark mode: the theme has one ground, and
 * the controls have to stand on the same one, so it follows the background colour
 * instead of being a separate question the admin can get wrong.
 */
export function deriveColorScheme(background: string): ColorScheme {
  const [r, g, b] = [1, 3, 5].map((at) =>
    parseInt(background.slice(at, at + 2), 16),
  )
  // Rec. 709 luminance, 0 (black) to 1 (white).
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5 ? 'dark' : 'light'
}

export type ColorScheme = z.infer<typeof ColorSchemeSchema>

/**
 * The family name stored inside a TrueType file, and whether it is a variable font.
 * The theme editor fills the family field from it, because the name is how the
 * screens, the postcard and the text editor find the uploaded file. Null when the
 * buffer is not a TrueType font (an .otf, a .woff, anything else).
 */
export function readTtfFontInfo(
  buffer: ArrayBuffer,
): { family: string | null; variable: boolean } | null {
  try {
    const view = new DataView(buffer)
    const version = view.getUint32(0)
    // 0x00010000 is TrueType; 'true' is the old Apple tag for the same outlines.
    if (version !== 0x00010000 && version !== 0x74727565) return null
    const tables = new Map<string, number>()
    for (let i = 0; i < view.getUint16(4); i++) {
      const record = 12 + i * 16
      const tag = String.fromCharCode(
        ...[0, 1, 2, 3].map((j) => view.getUint8(record + j)),
      )
      tables.set(tag, view.getUint32(record + 8))
    }
    return {
      family: readFamily(view, tables.get('name')),
      variable: tables.has('fvar'),
    }
  } catch {
    return null
  }
}

// Name ID 16 is the typographic family ("DM Sans"); ID 1 can carry the weight ("DM Sans Medium").
function readFamily(view: DataView, nameTable?: number): string | null {
  if (nameTable === undefined) return null
  const count = view.getUint16(nameTable + 2)
  const strings = nameTable + view.getUint16(nameTable + 4)
  const found = new Map<number, string>()
  for (let i = 0; i < count; i++) {
    const record = nameTable + 6 + i * 12
    const platformID = view.getUint16(record)
    const nameID = view.getUint16(record + 6)
    if ((nameID !== 1 && nameID !== 16) || found.has(nameID)) continue
    const length = view.getUint16(record + 8)
    const start = strings + view.getUint16(record + 10)
    // Windows (3) and Unicode (0) records are UTF-16BE; Mac (1) is one byte per char.
    const text =
      platformID === 1
        ? String.fromCharCode(
            ...Array.from({ length }, (_, j) => view.getUint8(start + j)),
          )
        : String.fromCharCode(
            ...Array.from({ length: length / 2 }, (_, j) =>
              view.getUint16(start + j * 2),
            ),
          )
    if (text.trim()) found.set(nameID, text.trim())
  }
  return found.get(16) ?? found.get(1) ?? null
}

// Same shape as customer_theme.font_files: a storage key per weight.
export const EndUserThemeFontFileSchema = z.object({
  key: z.string(),
  weight: z.string(),
})

// The home heading and greeting colours may stay unset (they then take the text colour);
// the editor sends an empty field as '', which is stored as null.
const OptionalHexColorSchema = z
  .union([HexColorSchema, z.literal('')])
  .nullable()
  .optional()
  .transform((value) => (value === '' ? null : value))

// Same for the heading font name, so a saved one can be cleared from the editor.
const OptionalFontFamilySchema = z
  .union([FontFamilySchema, z.literal('')])
  .nullable()
  .optional()
  .transform((value) => (value === '' ? null : value))

/** The stored row: keys, not URLs. Every field optional so a PUT can be partial. */
export const EndUserThemeSchema = z.object({
  ...colorFields,
  fontFamily: FontFamilySchema.nullable().optional(),
  fontFiles: z.array(EndUserThemeFontFileSchema).nullable().optional(),
  logoKey: z.string().nullable().optional(),
  heroImageKey: z.string().nullable().optional(),
  // Home screen only: the greeting and the headline. Unset keeps today's home.
  headingFontFamily: OptionalFontFamilySchema,
  headingFontFiles: z.array(EndUserThemeFontFileSchema).nullable().optional(),
  colorHeading: OptionalHexColorSchema,
  colorGreeting: OptionalHexColorSchema,
  headingUppercase: z.boolean().optional(),
})

export const UpdateEndUserThemeBodySchema = EndUserThemeSchema

export type EndUserThemeInput = z.infer<typeof EndUserThemeSchema>

/** What the screens and the print path receive: every role filled, URLs not keys. */
export const ResolvedEndUserThemeSchema = z.object({
  ...(Object.fromEntries(
    END_USER_THEME_COLOR_ROLES.map((role) => [role, HexColorSchema]),
  ) as Record<EndUserThemeColorRole, z.ZodString>),
  colorScheme: ColorSchemeSchema,
  fontFamily: FontFamilySchema,
  fontFiles: z.array(EndUserThemeFontFileSchema.extend({ url: z.string() })),
  // reason: campaigns launched while the theme had no logo carry snapshots without it.
  logoUrl: z.string().nullable().default(null),
  heroImageUrl: z.string().nullable(),
  // reason: the defaults let snapshots taken before these fields parse as today's home.
  headingFontFamily: FontFamilySchema.nullable().default(null),
  headingFontFiles: z
    .array(EndUserThemeFontFileSchema.extend({ url: z.string() }))
    .default([]),
  colorHeading: HexColorSchema.nullable().default(null),
  colorGreeting: HexColorSchema.nullable().default(null),
  headingUppercase: z.boolean().default(true),
})

export type ResolvedEndUserTheme = z.infer<typeof ResolvedEndUserThemeSchema>

/** campaign.end_user_theme_snapshot: the resolved theme with storage keys, so no host is frozen in. */
export const EndUserThemeSnapshotSchema = ResolvedEndUserThemeSchema.omit({
  logoUrl: true,
  heroImageUrl: true,
}).extend({
  fontFiles: z.array(EndUserThemeFontFileSchema),
  headingFontFiles: z.array(EndUserThemeFontFileSchema).default([]),
  logoKey: z.string().nullable(),
  heroImageKey: z.string().nullable(),
})

export type EndUserThemeSnapshot = z.infer<typeof EndUserThemeSnapshotSchema>

// The values of `.theme-default` in end-users default.css, as 6-digit hex.
export const DEFAULT_END_USER_THEME = {
  colorBackground: '#ffffff',
  colorBackgroundLight: '#ffffff',
  colorPrimary: '#1812ff',
  colorPrimaryForeground: '#ffffff',
  colorBaseButton: '#000000',
  colorSecondary: '#ffffff',
  colorForegroundLight: '#000000',
  colorSlate50: '#0f172a',
  colorBaseInput: '#e5e5e5',
  colorBorder: '#e5e5e5',
  colorMutedForeground: '#a3a3a3',
  colorDestructive: '#dc2626',
  fontFamily: 'Poppins',
} satisfies Omit<
  ResolvedEndUserTheme,
  | 'colorScheme'
  | 'fontFiles'
  | 'logoUrl'
  | 'heroImageUrl'
  | 'headingFontFamily'
  | 'headingFontFiles'
  | 'colorHeading'
  | 'colorGreeting'
  | 'headingUppercase'
>
